import {
  CartesianGrid,
  Legend,
  type LegendProps,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { formatCurrency } from '@/utils/currencyUtils';
import { checkboxColors } from '@/utils/colorUtils';
import { PayoutRevenue } from '@/types';
import type { ContentType, Payload } from 'recharts/types/component/DefaultLegendContent';
import { ChartTooltipWrapper } from '@/components/charts/ChartTooltip';
import PayoutsOverTimeLineGraphTooltipContent from './PayoutsOverTimeLineGraphTooltipContent';
import getMonthNameFromIndex from './getMonthNameFromIndex';

const PayoutsOverTimeLineGraph = ({
  data,
  networkCurrency,
}: {
  data: PayoutRevenue;
  networkCurrency: string;
}) => {
  const chartLineData = [...new Set(data.flatMap((item) => Object.keys(item.totals)))]
    .sort((a, b) => Number(b) - Number(a))
    .map((year, i) => ({ year, color: checkboxColors[i] }));

  return (
    <ResponsiveContainer minHeight={325} maxHeight={700} width={'100%'}>
      <LineChart data={data} role="img" aria-label="Line chart for payouts over time">
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="month"
          tickLine={false}
          tickFormatter={(tick) =>
            getMonthNameFromIndex({ index: parseInt(tick) - 1, variant: 'short' })
          }
          axisLine={false}
          tickMargin={10}
          tick={{ fill: '#8B8B8B', fontSize: '0.75rem' }}
          interval={'preserveStartEnd'}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={({ x, y, payload }) => {
            return (
              <g transform={`translate(${x},${y})`}>
                <text x={0} y={5} textAnchor="end" fill="#8B8B8B" fontSize="0.75rem">
                  {formatCurrency(payload.value, networkCurrency, true)}
                </text>
              </g>
            );
          }}
        />

        <Tooltip
          isAnimationActive={true}
          animationDuration={300}
          content={({ active, payload }: TooltipProps<number, string>) => (
            <ChartTooltipWrapper>
              {active && payload && payload[0] && (
                <PayoutsOverTimeLineGraphTooltipContent
                  payload={payload[0].payload}
                  chartLineData={chartLineData}
                />
              )}
            </ChartTooltipWrapper>
          )}
        />

        <Legend
          align="right"
          layout="vertical"
          verticalAlign="middle"
          iconType="circle"
          iconSize={12}
          wrapperStyle={{ right: '-2rem' }}
          content={CustomLegend as ContentType}
        />
        <CartesianGrid horizontal vertical={false} strokeWidth="1" stroke="#F0F0F9" />

        {chartLineData.map(({ year, color }, i) => (
          <Line
            key={`${i}-${year}`}
            dot={false}
            animationDuration={300}
            name={year}
            strokeWidth={2}
            type="linear"
            dataKey={`totals[${year}].amount`}
            stroke={color}
            fillOpacity={1}
            fill="url(#colorUv)"
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default PayoutsOverTimeLineGraph;

const CustomLegend = (props: LegendProps) => {
  const { payload } = props;

  return (
    <ul className="flex flex-col gap-6">
      {payload?.map((entry: Payload, index: number) => (
        <li key={`legend-item-${index}`} className="inline-flex items-center gap-1">
          <figure style={{ backgroundColor: entry.color }} className="w-3 h-3 rounded-full" />
          <span className="text-sm text-textDarkGrey">{entry.value}</span>
        </li>
      ))}
    </ul>
  );
};
